import React, { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Card, Alert, Tag, Select, DatePicker, Radio, Input, Space, Row, Col } from 'antd';
import { ReloadOutlined, FilterOutlined, ClearOutlined, LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../auth/AuthContext';
import './../App.css';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

function App() {
  const { logout } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('15');
  const [rangeType, setRangeType] = useState('preset');
  const [customRange, setCustomRange] = useState(null);
  const [metadata, setMetadata] = useState({
    actions: [],
    rules: [],
    countries: [],
    responseCodes: []
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    action: ['BLOCK'],
    country: [],
    terminating_rule_id: [],
    response_code_sent: [],
    client_ip: '',
    uri: ''
  });

  // Logout handler
  const handleLogout = () => {
    logout();
  };

  // Memoize applyFilters function
  const applyFilters = useCallback(() => {
    let filtered = [...logs];

    // Apply each filter with CloudWatch format
    if (filters.action.length > 0) {
      filtered = filtered.filter(log => filters.action.includes(log.action));
    }
    if (filters.country.length > 0) {
      filtered = filtered.filter(log => filters.country.includes(log.httpRequest.country));
    }
    if (filters.terminating_rule_id.length > 0) {
      filtered = filtered.filter(log => filters.terminating_rule_id.includes(log.terminatingRuleId));
    }
    if (filters.response_code_sent.length > 0) {
      filtered = filtered.filter(log => filters.response_code_sent.includes(log.responseCodeSent.toString()));
    }
    if (filters.client_ip) {
      filtered = filtered.filter(log => 
        log.httpRequest.clientIp.toLowerCase().includes(filters.client_ip.toLowerCase())
      );
    }
    if (filters.uri) {
      filtered = filtered.filter(log => 
        log.httpRequest.uri.toLowerCase().includes(filters.uri.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  }, [logs, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const clearFilters = () => {
    setFilters({
      action: [],
      country: [],
      terminating_rule_id: [],
      response_code_sent: [],
      client_ip: '',
      uri: ''
    });
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let queryParams = '';
  
      if (rangeType === 'preset') {
        queryParams = `timeframe=${timeframe}`;
      } else {
        if (!customRange || !customRange[0] || !customRange[1]) {
          throw new Error('Please select a valid date range');
        }
        queryParams = `customStartTime=${customRange[0].valueOf()}&customEndTime=${customRange[1].valueOf()}`;
      }
  
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
  
      const response = await fetch(`${process.env.REACT_APP_API_URL}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        mode: 'cors'  // Add this line
      });
  
      if (response.status === 401) {
        logout(); // Use the logout function from AuthContext
        throw new Error('Session expired. Please log in again.');
      }
  
      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.status}`);
      }
  
      const data = await response.json();
      
      // Process logs for display
      const processedLogs = data.logs.map(log => ({
        ...log,
        timestamp_date: new Date(log.timestamp || log.rawTimestamp),
        key: log.httpRequest.requestId,
        full_url: `${log.httpRequest.headers.find(h => h.name === 'Host')?.value || ''}${log.httpRequest.uri}${log.httpRequest.args ? `?${log.httpRequest.args}` : ''}`
      }));
      
      setLogs(processedLogs);
      setMetadata(data.metadata);
      setError(null);
    } catch (err) {
      console.error('Error in fetchLogs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'timestamp_date',
      key: 'timestamp',
      sorter: (a, b) => a.timestamp_date - b.timestamp_date,
      render: (timestamp_date) => (
        <div>
          <div>{timestamp_date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })}</div>
          <div className="small-text">
            {timestamp_date.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
              fractionalSecondDigits: 3,
              hour12: true
            })}
          </div>
        </div>
      ),
      width: 160,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => {
        let color = 'green';
        if (action === 'BLOCK') color = 'red';
        else if (action === 'COUNT') color = 'orange';
        return <Badge color={color} text={action} />;
      },
      width: 100,
    },
    {
      title: 'Client IP',
      dataIndex: ['httpRequest', 'clientIp'],
      key: 'clientIp',
      width: 130,
    },
    {
      title: 'Country',
      dataIndex: ['httpRequest', 'country'],
      key: 'country',
      width: 100,
    },
    {
      title: 'Request',
      key: 'request',
      render: (_, record) => (
        <div>
          <div>
            <strong>{record.httpRequest.httpMethod}</strong> 
            {record.httpRequest.uri}
            {record.httpRequest.args ? `?${record.httpRequest.args}` : ''}
          </div>
          <div className="small-text">{record.httpRequest.httpVersion}</div>
        </div>
      ),
    },
    {
      title: 'Terminating Rule',
      dataIndex: 'terminatingRuleId',
      key: 'terminatingRuleId',
      render: (rule, record) => rule ? (
        <div>
          <div>{rule}</div>
          <div className="small-text">{record.terminatingRuleType}</div>
        </div>
      ) : <span className="text-muted">None</span>,
    },
    {
      title: 'Response',
      dataIndex: 'responseCodeSent',
      key: 'responseCodeSent',
      width: 100,
      render: (code) => {
        let color = 'green';
        if (code >= 400 && code < 500) color = 'orange';
        else if (code >= 500) color = 'red';
        return <Tag color={color}>{code}</Tag>;
      },
    },
  ];

  const expandedRowRender = (record) => {
    return (
      <div className="expanded-row">
        <div className="expanded-section">
          <h4>Request Details</h4>
          <table className="detail-table">
            <tbody>
              <tr>
                <td className="header-name">Full URL:</td>
                <td><code>{record.full_url}</code></td>
              </tr>
              <tr>
                <td className="header-name">Method:</td>
                <td>{record.httpRequest.httpMethod}</td>
              </tr>
              <tr>
                <td className="header-name">HTTP Version:</td>
                <td>{record.httpRequest.httpVersion}</td>
              </tr>
              <tr>
                <td className="header-name">JA3 Fingerprint:</td>
                <td>{record.ja3Fingerprint}</td>
              </tr>
              <tr>
                <td className="header-name">JA4 Fingerprint:</td>
                <td>{record.ja4Fingerprint}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="expanded-section">
          <h4>Request Headers</h4>
          {record.httpRequest.headers && record.httpRequest.headers.length > 0 ? (
            <table className="detail-table">
              <tbody>
                {record.httpRequest.headers.map((header, i) => (
                  <tr key={i}>
                    <td className="header-name">{header.name}</td>
                    <td>{header.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted">No headers recorded</p>
          )}
        </div>

        {record.labels && record.labels.length > 0 && (
          <div className="expanded-section">
            <h4>Labels</h4>
            <div>
              {record.labels.map((label, index) => (
                <Tag key={index} color="blue" style={{ margin: '0 4px 4px 0' }}>
                  {label.name}
                </Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="app-container">
      {/* Logout Button */}
      <Button 
        onClick={handleLogout}
        type="primary"
        danger
        icon={<LogoutOutlined />}
        style={{ 
          position: 'absolute', 
          top: 16, 
          right: 16 
        }}
      >
        Logout
      </Button>

      <h1>WAF Logs Dashboard</h1>
      
      {error && <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      
      <Card className="filter-card">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div className="time-filter">
              <Radio.Group 
                value={rangeType} 
                onChange={(e) => setRangeType(e.target.value)}
                style={{ marginRight: '16px' }}
              >
                <Radio.Button value="preset">Preset Ranges</Radio.Button>
                <Radio.Button value="custom">Custom Range</Radio.Button>
              </Radio.Group>

              {rangeType === 'preset' ? (
                <Select
                  value={timeframe}
                  onChange={setTimeframe}
                  style={{ width: 200 }}
                >
                  <Option value="15">Last 15 minutes</Option>
                  <Option value="30">Last 30 minutes</Option>
                  <Option value="60">Last 1 hour</Option>
                  <Option value="360">Last 6 hours</Option>
                  <Option value="1440">Last 24 hours</Option>
                </Select>
              ) : (
                <RangePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  value={customRange}
                  onChange={setCustomRange}
                  style={{ width: 400 }}
                />
              )}

              <Button 
                type="primary"
                onClick={fetchLogs}
                loading={loading}
                icon={<ReloadOutlined />}
                style={{ marginLeft: '16px' }}
              >
                Fetch Logs
              </Button>
            </div>
          </Col>

          <Col span={24}>
            <Card title={
              <Space>
                <FilterOutlined /> Filters
                <Button 
                  type="link" 
                  icon={<ClearOutlined />} 
                  onClick={clearFilters}
                  size="small"
                >
                  Clear All
                </Button>
              </Space>
            }>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Filter by Action"
                    value={filters.action}
                    onChange={value => setFilters({ ...filters, action: value })}
                  >
                    {metadata.actions.map(action => (
                      <Option key={action} value={action}>{action}</Option>
                    ))}
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Filter by Country"
                    value={filters.country}
                    onChange={value => setFilters({ ...filters, country: value })}
                  >
                    {metadata.countries.map(country => (
                      <Option key={country} value={country}>{country}</Option>
                    ))}
                  </Select>
                </Col>
                
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Filter by Rule"
                    value={filters.terminating_rule_id}
                    onChange={value => setFilters({ ...filters, terminating_rule_id: value })}
                  >
                    {metadata.rules.map(rule => (
                      <Option key={rule} value={rule}>{rule}</Option>
                    ))}
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Filter by Response Code"
                    value={filters.response_code_sent}
                    onChange={value => setFilters({ ...filters, response_code_sent: value })}
                  >
                    {metadata.responseCodes.map(code => (
                      <Option key={code} value={code.toString()}>{code}</Option>
                    ))}
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                  <Search
                    placeholder="Search by Client IP"
                    allowClear
                    value={filters.client_ip}
                    onChange={e => setFilters({ ...filters, client_ip: e.target.value })}
                  />
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                  <Search
                    placeholder="Search by URI"
                    allowClear
                    value={filters.uri}
                    onChange={e => setFilters({ ...filters, uri: e.target.value })}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <div className="stats" style={{ marginTop: '16px' }}>
          <Tag color="blue">Total Logs: {filteredLogs.length}</Tag>
          <Tag color="red">Blocked Requests: {filteredLogs.filter(l => l.action === 'BLOCK').length}</Tag>
          <Tag color="green">Allowed Requests: {filteredLogs.filter(l => l.action === 'ALLOW').length}</Tag>
          <Tag color="orange">Counted Requests: {filteredLogs.filter(l => l.action === 'COUNT').length}</Tag>
          {logs.length > 0 && rangeType === 'custom' && customRange && (
            <Tag color="purple">
              Time Range: {customRange[0].format('MMM DD, HH:mm')} - {customRange[1].format('MMM DD, HH:mm')}
            </Tag>
          )}
          {logs.length > 0 && rangeType === 'preset' && (
            <Tag color="purple">Time Range: Last {timeframe} minutes</Tag>
          )}
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={filteredLogs}
        loading={loading}
        expandable={{ expandedRowRender }}
        pagination={{ pageSize: 10 }}
        rowKey={record => record.httpRequest.requestId}
        scroll={{ x: 1200 }}
      />
    </div>
  );
}

export default App;